import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import { selectors } from '@uber/react-qna-lib';
import { styled } from 'baseui';
import { useStyletron } from 'baseui';
import noop from 'lodash/noop';
import type { Logger } from '../..';
import type { SendEventT } from '../../analytics/types';
import { CollapsibleSidePanel } from '../../common/collapsible-side-panel/collapsible-side-panel';
import QuestionnairePanel from '../../common/questionnaire-panel/questionnaire-panel';
import type { QuestionnaireT } from '../../common/types/types';
import type { AnnotationLabelT } from '../../image';
import type { InputDataT, onStoreChangeT, OutputAnnotationT } from '../../image/labelling';
import { getItemFromStorage, setItemInStorage } from '../../image/labelling/utils';
import type { BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT, SamModelImagePluginRequestT, SamModelPluginRequestT, SamModelVideoPluginRequestT, SAMSegmentImageData, SAMSegmentVideoData, TrackObjectsOutputT, TrackObjectsRequestT } from '../../image/types';
import { useRegisterHotKeys } from '../../index';
import ReviewSuggestionsSidePanel from '../components/review-suggestions-panel/review-suggestions-side-panel';
import { BASE_API_URL } from '../consts/base-url';
import { useStudioRef } from '../hooks/use-studio-ref';
import type { SuggestionSidePanelPropsT } from '../types/types';
import { getBoundingBoxTrackingData, getSamImageSegmentData, getSamVideoSegmentDataV2, trackObjectsAcrossKeyframes as trackObjectsAcrossKeyframesClient, transformSegmentationImageToPoints } from '../utils';
import ImageLabelStudioContainer from './studio-container';
const StudioContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  height: '100%',
  width: '100%',
  gap: $theme.sizing.scale300,
  backgroundColor: $theme.colors.backgroundPrimary
}));
type PropsT = {
  inputData: InputDataT;
  taskId?: number;
  annotationLabels: AnnotationLabelT[];
  onStoreChange: onStoreChangeT;
  onTrackingActiveChange?: (isActive: boolean) => void;
  sendEvent?: SendEventT;
  fetchQuestionnaire: (questionnaireId: string) => Promise<QnAStateType>;
  questionnaires: {
    [questionnaireId: string]: {
      questionnaire: QnAStateType;
      name: string;
    };
  } | undefined;
  taskLevelQuestionnaire?: (QuestionnaireT & {
    name: string;
  })[];
  onAnswerChange: (updatedAnswer: {
    [questionnaireName: string]: QnAStateType['answers'];
  }, questionnaireValid: boolean) => void;
  questionnairePreAnnotation: {
    [id: string]: QnAStateType;
  } | undefined;
  getYoloData?: () => OutputAnnotationT[][];
  getSignedUrl: () => Promise<string | undefined>;
  suggestionSidePanelProps?: SuggestionSidePanelPropsT;
  isReviewMode: boolean;
  logger?: Logger;
};
export type { StudioRef } from '../../image/types';
export const Studio = ({
  inputData,
  taskId,
  annotationLabels,
  questionnaires,
  questionnairePreAnnotation,
  taskLevelQuestionnaire,
  onStoreChange,
  onTrackingActiveChange,
  sendEvent,
  fetchQuestionnaire,
  onAnswerChange,
  getSignedUrl,
  suggestionSidePanelProps,
  isReviewMode,
  logger
}: PropsT) => {
  const studioRef = useStudioRef();
  const [css, theme] = useStyletron();
  const [questionnairePage, setQuestionnairePage] = useState(1);
  const {
    registerHotKeys
  } = useRegisterHotKeys();
  const taskLevelQuestionnaireList = useMemo(() => (questionnaires && taskLevelQuestionnaire?.map(questionnaire => {
    return {
      questionnaireId: questionnaire.questionnaireId,
      questionnaire: {
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        questionnaire: questionnaires[questionnaire.questionnaireId].questionnaire,
        name: questionnaire.name
      }
    };
  })) ?? [], [questionnaires, taskLevelQuestionnaire]);
  const currentQuestionnaire = taskLevelQuestionnaireList[questionnairePage - 1];
  const [currentAnswers, setCurrentAnswers] = useState<{
    [questionnaireName: string]: QnAStateType['answers'];
  }>({});
  const questionnaireValidityMap = useRef<{
    [questionnaireName: string]: boolean;
  }>({});
  const labelTitleToSAMInput = useRef<{
    [key: string]: SamModelVideoPluginRequestT;
  }>({});
  const processedMasksCache = useRef<{
    [labelTitle: string]: {
      [frameIndex: number]: string;
    };
  }>({});
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);
  const answersFromInput = currentQuestionnaire && questionnairePreAnnotation?.[currentQuestionnaire?.questionnaireId]?.answers;

  // Create a reference to store the mapping of trackIds to annotation UUIDs
  const trackIdToUuidMap = useRef<{
    [trackId: string]: string;
  }>({});
  const updatedQuestionnaire: {
    questionnaire: QnAStateType;
    name?: string;
  } | undefined = useMemo(() => taskLevelQuestionnaireList.length > 0 ? {
    // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
    name: taskLevelQuestionnaireList[questionnairePage - 1].questionnaire.name,
    questionnaire: {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      ...taskLevelQuestionnaireList[questionnairePage - 1].questionnaire.questionnaire,
      answers: {
        // @ts-expect-error todo(ts-migration) TS18048 'currentQuestionnaire' is possibly 'undefined'.
        ...currentQuestionnaire.questionnaire.questionnaire.answers,
        ...(answersFromInput ?? {}),
        // @ts-expect-error todo(ts-migration) TS18048 'currentQuestionnaire' is possibly 'undefined'.
        ...(currentAnswers[currentQuestionnaire.questionnaire.name] ?? {})
      }
    }
  } : undefined, [taskLevelQuestionnaireList, questionnairePage, answersFromInput, currentAnswers, currentQuestionnaire]);

  /**
   * Fetches the SAM image data points from the server using the /segment/image endpoint.
   * It has been implemented only for Image Segmentation for now.
   * TODO: Implement for Video as well.
   */
  const getSAMImageDataPoints = useCallback(async (input: SamModelImagePluginRequestT, _annotationTrackId?: string): Promise<SAMSegmentImageData[] | SAMSegmentVideoData> => {
    try {
      const response = await fetch(`${BASE_API_URL}/segment/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageUrl: input.imageUrl,
          segmentationType: input.imageSegmentationType,
          points: input.points,
          box: input.box
        })
      });

      // Get the mask/polygon from the response and extract the points
      const predictions = (await response.json()).predictions;
      const predictionPoints: SAMSegmentImageData[] = [];
      for (let i = 0; i < predictions.length; i++) {
        const {
          mask,
          points
        } = predictions[i];
        if (points) {
          predictionPoints.push({
            points: points.map(({
              x,
              y
            }) => [x, y])
          });
        } else if (mask) {
          const imgUrl = `data:image/png;base64,${mask}`;
          const transformedResponse = await transformSegmentationImageToPoints({
            imgData: imgUrl,
            color: 'white'
          });
          if (transformedResponse && 'points' in transformedResponse) {
            predictionPoints.push({
              points: transformedResponse.points
            });
          }
        }
      }
      return Promise.resolve(predictionPoints);
    } catch (error) {
      return Promise.reject(error);
    }
  }, []);
  const getSamSegmentData = useCallback(async (input: SamModelPluginRequestT, labelTitle?: string): Promise<SAMSegmentImageData | Error | null> => {
    if ('imageUrl' in input) {
      return getSamImageSegmentData(input);
    } else if (labelTitle && 'videoUrl' in input) {
      if (!processedMasksCache.current[labelTitle]) {
        processedMasksCache.current[labelTitle] = {};
      }
      if (processedMasksCache.current[labelTitle][input.frameIndex]) {
        const cachedMask = processedMasksCache.current[labelTitle][input.frameIndex];
        return {
          mask: cachedMask ?? ''
        };
      }
      const result = await getSamVideoSegmentDataV2(input);
      if (result instanceof Error) {
        return result;
      }
      if (result && result[0]) {
        labelTitleToSAMInput.current[labelTitle] = input;
        if (result[0].mask) {
          processedMasksCache.current[labelTitle][input.frameIndex] = result[0].mask;
          return {
            mask: result[0].mask
          };
        }
        if (result[0].points) {
          return {
            points: result[0].points
          };
        }
      }
      return null;
    }
    return null;
  }, []);
  const getAnnotationTrackingData = useCallback(async (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }): Promise<SAMSegmentVideoData | Error> => {
    const startFrame = frameRange?.start ?? 0;
    const endFrame = frameRange?.end ?? 0;
    if (!processedMasksCache.current[labelTitle]) {
      processedMasksCache.current[labelTitle] = {};
    }
    const samInput = labelTitleToSAMInput.current[labelTitle];
    if (!samInput) {
      return Promise.resolve([]);
    }
    const updatedInput: SamModelVideoPluginRequestT = {
      ...samInput,
      frameRange: {
        startFrame,
        endFrame
      }
    };
    const predictions = await getSamVideoSegmentDataV2(updatedInput);
    if (predictions instanceof Error) {
      return predictions;
    }
    if (!predictions) return Promise.resolve([]);
    const result = predictions.map((imageData, index) => {
      const frameIndex = startFrame + index;
      const mask = imageData.mask ?? '';
      if (mask) {
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        processedMasksCache.current[labelTitle][frameIndex] = mask;
        return {
          mask
        };
      }
      return {
        mask: ''
      };
    });
    return result;
  }, []);
  const getBoundingBoxData = useCallback(async (input: BoundingBoxTrackingInputT): Promise<BoundingBoxTrackingOutputT | Error> => {
    const result = await getBoundingBoxTrackingData(input);
    return result;
  }, []);

  // Bind the batch tracking client to this task here (where `taskId` is known) so the shared
  // studio never has to carry `taskId` — it just receives a task-scoped tracking capability.
  const trackObjectsAcrossKeyframes = useCallback((input: TrackObjectsRequestT): Promise<TrackObjectsOutputT | Error> => {
    if (taskId === undefined) {
      return Promise.resolve(new Error('Cannot track objects without a task id'));
    }
    return trackObjectsAcrossKeyframesClient({
      ...input,
      taskId
    });
  }, [taskId]);
  const showCollapsiblePanel = useMemo(() => {
    return suggestionSidePanelProps?.showSuggestionsPanel || updatedQuestionnaire;
  }, [suggestionSidePanelProps?.showSuggestionsPanel, updatedQuestionnaire]);
  const questionnaireSidePanel = <QuestionnairePanel currentPage={0} totalPages={taskLevelQuestionnaireList.length} currentQuestionnaire={{
    questionnaire: {
      questions: updatedQuestionnaire?.questionnaire.questions ?? {},
      topQuestionIds: updatedQuestionnaire?.questionnaire.topQuestionIds ?? [],
      answers: updatedQuestionnaire?.questionnaire.answers ?? {}
    },
    name: updatedQuestionnaire?.name ?? ''
  }} handlePageChange={nextPage => {
    setQuestionnairePage(nextPage);
  }} updateQuestionnaire={updatedQuestionnaire => {
    const updatedAnswer = {
      [updatedQuestionnaire.name]: updatedQuestionnaire.questionnaire.answers
    };
    setCurrentAnswers(updatedAnswer);
    questionnaireValidityMap.current[updatedQuestionnaire.name] = !selectors.hasValidationErrors(updatedQuestionnaire.questionnaire);
    const anyQuestionnaireInvalid = Object.entries(questionnaireValidityMap.current).some(([, isValid]) => !isValid);
    onAnswerChange(updatedAnswer, !anyQuestionnaireInvalid);
  }} showHeading={true} />;
  const suggestionsSidePanel = suggestionSidePanelProps ? <ReviewSuggestionsSidePanel trackingDetails={suggestionSidePanelProps.trackingDetails || []} sendEvent={sendEvent || noop} suggestionsError={suggestionSidePanelProps.suggestionsError} setTrackingDetails={suggestionSidePanelProps.setTrackingDetails} onEvaluate={suggestionSidePanelProps.evaluateSuggestionsData} panelState={suggestionSidePanelProps.panelState} onCloseSidePanel={() => {
    suggestionSidePanelProps?.setShowSuggestionsPanel(false);
  }} /> : null;
  return <StudioContainer data-testid="studio-container">
      <ImageLabelStudioContainer studioRef={studioRef} isSidePanelCollapsed={isSidePanelCollapsed} updatedQuestionnaire={!!updatedQuestionnaire} suggestionSidePanelProps={suggestionSidePanelProps} inputData={inputData} annotationLabels={annotationLabels} onStoreChange={onStoreChange} onTrackingActiveChange={onTrackingActiveChange} sendEvent={sendEvent} logger={logger} fetchQuestionnaire={fetchQuestionnaire} setItemInStorage={setItemInStorage} getItemFromStorage={getItemFromStorage} getSuperpixelImageRegionsData={() => {
      throw new Error('Function not implemented.');
    }} isFeatureEnabled={() => {
      return Promise.resolve({
        enabled: true
      });
    }} renderPDFFrame={async () => {}} getPdfPage={async () => ({
      view: [0, 0, 0, 0]
    })} getSAMImageDataPoints={getSAMImageDataPoints} getSamSegmentData={getSamSegmentData} getAnnotationTrackingData={getAnnotationTrackingData} getBoundingBoxData={getBoundingBoxData} trackObjectsAcrossKeyframes={trackObjectsAcrossKeyframes} getSignedUrl={getSignedUrl} registerHotKeys={registerHotKeys} isReviewMode={isReviewMode} />

      {showCollapsiblePanel && <CollapsibleSidePanel onCollapseChange={isPanelCollapsed => {
      setIsSidePanelCollapsed(isPanelCollapsed);
    }} className={css({
      display: 'flex',
      backgroundColor: `${theme.colors.backgroundPrimary}`
    })}>
          {suggestionSidePanelProps?.showSuggestionsPanel ? suggestionsSidePanel : updatedQuestionnaire ? questionnaireSidePanel : null}
        </CollapsibleSidePanel>}
    </StudioContainer>;
};